import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles, Button, IconButton, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Select, MenuItem, InputLabel,
  FormControl, Tooltip, Typography, InputAdornment, Divider,
  TableFooter, Switch, FormControlLabel, CircularProgress,
  Tabs, Tab, Box,
} from "@material-ui/core";
import {
  Add, Edit, Delete, Search, Visibility, AddCircle, RemoveCircle,
  PictureAsPdf, WhatsApp, Schedule as ScheduleIcon, CloudUpload,
} from "@material-ui/icons";
import jsPDF from "jspdf";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2), flexWrap: "wrap", alignItems: "center" },
  paper: { overflowX: "auto" },
  table: { minWidth: 700 },
  itemRow: { background: theme.palette.action.hover },
  tabPanel: { paddingTop: theme.spacing(2) },
  logoBox: {
    display: "flex", alignItems: "center", gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    background: theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  logoPreview: { height: 48, maxWidth: 160, objectFit: "contain", borderRadius: 2 },
}));

const STATUS_LIST = [
  { value: "rascunho", label: "Rascunho" },
  { value: "enviada", label: "Enviada" },
  { value: "em_negociacao", label: "Em Negociação" },
  { value: "aprovada", label: "Aprovada" },
  { value: "recusada", label: "Recusada" },
  { value: "cancelada", label: "Cancelada" },
];

const STATUS_COLORS = {
  rascunho: "#9e9e9e", enviada: "#2196f3", em_negociacao: "#ff9800",
  aprovada: "#4caf50", recusada: "#f44336", cancelada: "#757575",
};

const makeEmptyItem = () => ({ productId: "", description: "", quantity: 1, unitPrice: 0, discount: 0, total: 0 });
const makeEmptyForm = () => ({
  title: "", clientId: "", responsibleId: "", status: "rascunho",
  issueDate: new Date().toISOString().slice(0, 10), validUntil: "", paymentTerms: "",
  intro: "", notes: "", orientacoes: "", termos: "",
  discountValue: 0, items: [],
});

const calcItemTotal = (item) => {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unitPrice) || 0;
  const disc = Number(item.discount) || 0;
  return parseFloat((qty * price * (1 - disc / 100)).toFixed(2));
};

const fmt = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtNum = (v) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
const statusLabel = (s) => STATUS_LIST.find((x) => x.value === s)?.label || (s || "");

const substituteVars = (text, client, issueDate, validUntil) => {
  if (!text) return "";
  return text
    .replace(/\{\{cliente_nome\}\}/g, client?.name || "")
    .replace(/\{\{cliente_empresa\}\}/g, client?.company || "")
    .replace(/\{\{cliente_documento\}\}/g, client?.document || "")
    .replace(/\{\{cliente_email\}\}/g, client?.email || "")
    .replace(/\{\{cliente_telefone\}\}/g, client?.phone || "")
    .replace(/\{\{cliente_cidade\}\}/g, client?.city ? `${client.city}/${client.state || ""}` : "")
    .replace(/\{\{data_hoje\}\}/g, issueDate || "")
    .replace(/\{\{data_validade\}\}/g, validUntil || "");
};

const hexToRgb = (hex) => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [33, 97, 220];
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box>{children}</Box> : null;
}

// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
const buildProposalPDF = (proposal, logoDataUrl, primaryColor) => {
  const doc = new jsPDF("p", "mm", "a4");
  const PW = 210, PH = 297, M = 14, CW = PW - 2 * M;
  let y = 0;
  const [cr, cg, cb] = hexToRgb(primaryColor || "#2161dc");

  const sub = (text) => substituteVars(text, proposal.client, proposal.issueDate, proposal.validUntil);

  const newPage = () => { doc.addPage(); y = M; };
  const checkY = (h = 8) => { if (y + h > PH - M) newPage(); };

  const sectionTitle = (title) => {
    checkY(14);
    doc.setDrawColor(200, 200, 200); doc.line(M, y, PW - M, y); y += 5;
    doc.setFontSize(10); doc.setTextColor(cr, cg, cb); doc.setFont("helvetica", "bold");
    doc.text(title.toUpperCase(), M, y); y += 6;
    doc.setTextColor(40, 40, 40); doc.setFont("helvetica", "normal");
  };

  const multilineText = (text, fontSize = 9) => {
    if (!text || !text.trim()) return;
    doc.setFontSize(fontSize); doc.setFont("helvetica", "normal"); doc.setTextColor(40, 40, 40);
    const lines = doc.splitTextToSize(text.trim(), CW);
    lines.forEach((line) => { checkY(5); doc.text(line, M, y); y += 5; });
    y += 2;
  };

  // Header bar
  doc.setFillColor(cr, cg, cb);
  doc.rect(0, 0, PW, 30, "F");

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", M, 4, 0, 22);
    } catch (_) {}
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18); doc.setFont("helvetica", "bold");
  doc.text("PROPOSTA COMERCIAL", PW / 2, 14, { align: "center" });
  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text(proposal.proposalNumber || "", PW / 2, 24, { align: "center" });
  y = 36;

  // Status chip
  const sColor = STATUS_COLORS[proposal.status] || "#555";
  const rgb = sColor.match(/\d+/g)?.map(Number) || [80, 80, 80];
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  doc.roundedRect(M, y, 42, 7, 2, 2, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("helvetica", "bold");
  doc.text(statusLabel(proposal.status).toUpperCase(), M + 21, y + 4.5, { align: "center" });
  y += 12;

  // Info block
  doc.setTextColor(40, 40, 40); doc.setFontSize(9); doc.setFont("helvetica", "normal");
  const col1 = M, col2 = PW / 2 + 2;
  const row = (label, val, cx, cy) => {
    doc.setFont("helvetica", "bold"); doc.text(label, cx, cy);
    doc.setFont("helvetica", "normal"); doc.text(String(val || "—"), cx + 30, cy);
  };
  row("Título:", proposal.title || "—", col1, y);
  row("Emissão:", proposal.issueDate || "—", col2, y); y += 6;
  row("Cliente:", proposal.client ? proposal.client.name : "—", col1, y);
  row("Válida até:", proposal.validUntil || "—", col2, y); y += 6;
  if (proposal.client?.company) {
    doc.setFont("helvetica", "bold"); doc.text("Empresa:", col1, y);
    doc.setFont("helvetica", "normal"); doc.text(proposal.client.company, col1 + 30, y);
    y += 6;
  }
  row("Responsável:", proposal.responsible ? proposal.responsible.name : "—", col1, y);
  row("Pagamento:", proposal.paymentTerms || "—", col2, y); y += 10;

  // Intro
  if (proposal.intro) {
    sectionTitle("Apresentação");
    multilineText(sub(proposal.intro));
  }

  // Items table
  sectionTitle("Serviços / Produtos");
  doc.setFillColor(50, 50, 50);
  doc.rect(M, y, CW, 7, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont("helvetica", "bold");
  const cols = [M + 2, M + 80, M + 118, M + 140, M + 162, M + 185];
  doc.text("Descrição", cols[0], y + 4.5);
  doc.text("Qtd", cols[1], y + 4.5, { align: "right" });
  doc.text("Preço Unit.", cols[2], y + 4.5, { align: "right" });
  doc.text("Desc %", cols[3], y + 4.5, { align: "right" });
  doc.text("Total", cols[4], y + 4.5, { align: "right" });
  y += 8;

  (proposal.items || []).forEach((item, i) => {
    checkY(7);
    if (i % 2 === 0) { doc.setFillColor(248, 249, 250); doc.rect(M, y - 1, CW, 7, "F"); }
    doc.setTextColor(40, 40, 40); doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text(String(item.description || "").substring(0, 55), cols[0], y + 4);
    doc.text(fmtNum(item.quantity), cols[1], y + 4, { align: "right" });
    doc.text(fmt(item.unitPrice), cols[2], y + 4, { align: "right" });
    doc.text(String(Number(item.discount) || 0) + "%", cols[3], y + 4, { align: "right" });
    doc.text(fmt(item.total), cols[4], y + 4, { align: "right" });
    y += 7;
  });

  // Totals
  y += 4;
  doc.setDrawColor(200, 200, 200); doc.line(M, y, PW - M, y); y += 6;
  const labelX = M + CW - 52, valX = M + CW;
  const subtotal = (proposal.items || []).reduce((a, i) => a + calcItemTotal(i), 0);
  const discV = Number(proposal.discountValue) || 0;
  const total = subtotal - discV;
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80, 80, 80);
  doc.text("Subtotal:", labelX, y); doc.text(fmt(subtotal), valX, y, { align: "right" }); y += 6;
  if (discV > 0) {
    doc.text("Desconto:", labelX, y); doc.text("- " + fmt(discV), valX, y, { align: "right" }); y += 6;
  }
  doc.setFillColor(cr, cg, cb); doc.rect(M + CW - 60, y - 1, 62, 9, "F");
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("TOTAL:", labelX, y + 5.5); doc.text(fmt(total), valX, y + 5.5, { align: "right" }); y += 14;

  // Notes / Observações
  if (proposal.notes) {
    sectionTitle("Observações");
    multilineText(sub(proposal.notes));
  }

  // Orientações
  if (proposal.orientacoes) {
    sectionTitle("Orientações ao Cliente");
    multilineText(sub(proposal.orientacoes));
  }

  // Termos
  if (proposal.termos) {
    sectionTitle("Termos e Condições");
    multilineText(sub(proposal.termos));
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8); doc.setTextColor(150, 150, 150); doc.setFont("helvetica", "normal");
    doc.text(`Página ${p} de ${pageCount}`, PW / 2, PH - 6, { align: "center" });
  }

  return doc;
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export default function Proposals() {
  const classes = useStyles();
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [formTab, setFormTab] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [form, setForm] = useState(makeEmptyForm);
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });

  // Logo + color state
  const [logoPath, setLogoPath] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#2161dc");
  const logoInputRef = useRef();

  // WhatsApp send dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sendProposal, setSendProposal] = useState(null);
  const [sendForm, setSendForm] = useState({ phone: "", whatsappId: "", message: "", sendAt: "", schedule: false });
  const [sending, setSending] = useState(false);

  const formRef = useRef(form);
  formRef.current = form;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proposals", {
        params: { searchParam: search || undefined, status: statusFilter || undefined },
      });
      setProposals(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erro ao carregar propostas");
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/proposal-clients"),
      api.get("/users"),
      api.get("/proposal-products", { params: { onlyActive: "true" } }),
      api.get("/proposal-templates"),
      api.get("/whatsapp/"),
      api.get("/proposals/settings/logo"),
    ]).then(([c, u, p, t, w, logo]) => {
      setClients(Array.isArray(c.data) ? c.data : []);
      const rawUsers = u.data?.users ?? u.data;
      setUsers(Array.isArray(rawUsers) ? rawUsers : []);
      setProducts(Array.isArray(p.data) ? p.data : []);
      setTemplates(Array.isArray(t.data) ? t.data : []);
      const wList = Array.isArray(w.data) ? w.data : (w.data?.whatsapps ?? []);
      setWhatsapps(wList.filter((ww) => ww.status === "CONNECTED"));
      if (logo.data?.logoPath) setLogoPath(logo.data.logoPath);
      if (logo.data?.color) setPrimaryColor(logo.data.color);
    }).catch(() => {});
  }, []);

  // Load logo as data URL for PDF use
  useEffect(() => {
    if (!logoPath) { setLogoDataUrl(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      setLogoDataUrl(canvas.toDataURL("image/png"));
    };
    img.onerror = () => setLogoDataUrl(null);
    img.src = `${process.env.REACT_APP_BACKEND_URL}/public/${logoPath}?t=${Date.now()}`;
  }, [logoPath]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file, file.name);
      const { data } = await api.post("/proposals/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoPath(data.logoPath);
      toast.success("Logotipo atualizado!");
    } catch {
      toast.error("Erro ao enviar logotipo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleColorChange = async (hex) => {
    setPrimaryColor(hex);
    try {
      await api.put("/proposals/settings", { color: hex });
    } catch {
      toast.error("Erro ao salvar cor");
    }
  };

  const handleOpen = (proposal = null) => {
    setEditing(proposal);
    setSelectedTemplate("");
    setFormTab(0);
    if (proposal) {
      setForm({
        title: proposal.title || "",
        clientId: proposal.clientId || "",
        responsibleId: proposal.responsibleId || "",
        status: proposal.status || "rascunho",
        issueDate: proposal.issueDate || "",
        validUntil: proposal.validUntil || "",
        paymentTerms: proposal.paymentTerms || "",
        intro: proposal.intro || "",
        notes: proposal.notes || "",
        orientacoes: proposal.orientacoes || "",
        termos: proposal.termos || "",
        discountValue: Number(proposal.discountValue) || 0,
        items: (proposal.items || []).map((i) => ({
          productId: i.productId || "",
          description: i.description || "",
          quantity: Number(i.quantity) || 0,
          unitPrice: Number(i.unitPrice) || 0,
          discount: Number(i.discount) || 0,
          total: Number(i.total) || 0,
        })),
      });
    } else {
      setForm(makeEmptyForm());
    }
    setOpen(true);
  };

  const handleView = (proposal) => { setViewData(proposal); setViewOpen(true); };

  const handleSave = async () => {
    try {
      const current = formRef.current;
      const payload = {
        ...current,
        clientId: current.clientId || undefined,
        responsibleId: current.responsibleId || undefined,
        discountValue: Number(current.discountValue) || 0,
        items: current.items,
      };
      if (editing) {
        await api.put(`/proposals/${editing.id}`, payload);
        toast.success("Proposta atualizada");
      } else {
        await api.post("/proposals", payload);
        toast.success("Proposta criada");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar proposta");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/proposals/${deleteModal.id}`);
      toast.success("Proposta deletada");
      setDeleteModal({ open: false, id: null, name: "" });
      load();
    } catch {
      toast.error("Erro ao deletar proposta");
    }
  };

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm({ ...formRef.current, [key]: value });
  };

  const addItem = () => {
    const current = formRef.current;
    setForm({ ...current, items: [...current.items, makeEmptyItem()] });
  };

  const removeItem = (idx) => {
    const current = formRef.current;
    setForm({ ...current, items: current.items.filter((_, i) => i !== idx) });
  };

  const updateItem = (idx, key, value) => {
    const current = formRef.current;
    const items = current.items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [key]: value };
      updated.total = calcItemTotal(updated);
      return updated;
    });
    setForm({ ...current, items });
  };

  const selectProduct = (idx, productId) => {
    const current = formRef.current;
    const product = products.find((p) => String(p.id) === String(productId));
    if (!product) {
      setForm({ ...current, items: current.items.map((item, i) => (i === idx ? { ...item, productId: "" } : item)) });
      return;
    }
    const items = current.items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, productId: product.id, description: product.name, unitPrice: Number(product.price) || 0 };
      updated.total = calcItemTotal(updated);
      return updated;
    });
    setForm({ ...current, items });
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((t) => String(t.id) === String(templateId));
    setSelectedTemplate(templateId);
    if (!template) return;
    const current = formRef.current;
    const client = clients.find((c) => String(c.id) === String(current.clientId));
    const today = new Date().toISOString().slice(0, 10);
    const validDate = new Date();
    validDate.setDate(validDate.getDate() + (Number(template.validDays) || 30));
    const validUntil = validDate.toISOString().slice(0, 10);
    const sub = (text) => substituteVars(text, client, today, validUntil);
    setForm({
      ...current,
      title: sub(template.title || current.title),
      paymentTerms: sub(template.paymentTerms || ""),
      intro: sub(template.intro || ""),
      notes: sub(template.notes || ""),
      orientacoes: sub(template.orientacoes || ""),
      termos: sub(template.termos || ""),
      validUntil,
      status: template.defaultStatus || current.status,
    });
  };

  const handleDownloadPDF = (proposal) => {
    try {
      const doc = buildProposalPDF(proposal, logoDataUrl, primaryColor);
      doc.save(`${proposal.proposalNumber || "proposta"}.pdf`);
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  const openSendDialog = (proposal) => {
    setSendProposal(proposal);
    const nowPlus2 = new Date(Date.now() + 2 * 60 * 1000);
    const localISO = new Date(nowPlus2.getTime() - nowPlus2.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setSendForm({
      phone: proposal.client?.phone || "",
      whatsappId: whatsapps.length === 1 ? String(whatsapps[0].id) : "",
      message: `Segue a proposta comercial ${proposal.proposalNumber}.`,
      sendAt: localISO,
      schedule: false,
    });
    setSendOpen(true);
  };

  const handleSendWhatsApp = async () => {
    if (!sendForm.whatsappId) { toast.warning("Selecione uma conexão WhatsApp"); return; }
    if (!sendForm.phone) { toast.warning("Informe o telefone"); return; }
    setSending(true);
    try {
      const doc = buildProposalPDF(sendProposal, logoDataUrl, primaryColor);
      const pdfBlob = doc.output("blob");
      const sendAt = sendForm.schedule ? sendForm.sendAt : new Date(Date.now() + 2 * 60 * 1000).toISOString();
      const fd = new FormData();
      fd.append("pdf", pdfBlob, `${sendProposal.proposalNumber || "proposta"}.pdf`);
      fd.append("whatsappId", sendForm.whatsappId);
      fd.append("phone", sendForm.phone.replace(/\D/g, ""));
      fd.append("message", sendForm.message || `Segue a proposta ${sendProposal.proposalNumber}.`);
      fd.append("sendAt", sendAt);
      await api.post(`/proposals/${sendProposal.id}/send-whatsapp`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(sendForm.schedule ? "Envio agendado!" : "Proposta enviada para a fila!");
      setSendOpen(false);
    } catch {
      toast.error("Erro ao enviar proposta por WhatsApp");
    } finally {
      setSending(false);
    }
  };

  const subtotal = form.items.reduce((acc, i) => acc + calcItemTotal(i), 0);
  const discountValue = Number(form.discountValue) || 0;
  const total = subtotal - discountValue;

  return (
    <div className={classes.root}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <Typography variant="h5">Propostas Comerciais</Typography>
      </div>

      {/* Logo upload section */}
      <div className={classes.logoBox}>
        {logoPath ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/public/${logoPath}`}
            alt="Logo" className={classes.logoPreview}
          />
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
            Sem logotipo configurado
          </Typography>
        )}
        <input
          ref={logoInputRef} type="file" accept="image/*" hidden
          onChange={handleLogoUpload}
        />
        <Button
          size="small" variant="outlined" color="primary"
          startIcon={logoUploading ? <CircularProgress size={14} /> : <CloudUpload />}
          onClick={() => logoInputRef.current?.click()}
          disabled={logoUploading}
        >
          {logoPath ? "Alterar Logotipo" : "Enviar Logotipo"}
        </Button>
        <Divider orientation="vertical" flexItem />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{ width: 28, height: 28, borderRadius: 4, background: primaryColor, border: "2px solid #ccc", cursor: "pointer", position: "relative" }}
            title="Cor principal do PDF"
          >
            <input
              type="color" value={primaryColor}
              onChange={(e) => handleColorChange(e.target.value)}
              style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
            />
          </div>
          <Typography variant="caption" color="textSecondary">Cor principal do PDF</Typography>
        </div>
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto" }}>
          Logotipo e cor são exibidos no cabeçalho do PDF
        </Typography>
      </div>

      <div className={classes.toolbar}>
        <TextField
          size="small" variant="outlined" placeholder="Buscar título, número..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          style={{ minWidth: 250 }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {STATUS_LIST.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
          Nova Proposta
        </Button>
      </div>

      <Paper className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Emissão</TableCell>
              <TableCell>Validade</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proposals.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.proposalNumber}</TableCell>
                <TableCell>{p.title}</TableCell>
                <TableCell>{p.client ? p.client.name : "—"}</TableCell>
                <TableCell>{p.responsible ? p.responsible.name : "—"}</TableCell>
                <TableCell>{p.issueDate || "—"}</TableCell>
                <TableCell>{p.validUntil || "—"}</TableCell>
                <TableCell align="right">{fmt(p.total)}</TableCell>
                <TableCell align="center">
                  <span style={{ color: STATUS_COLORS[p.status] || "#555", fontWeight: 600 }}>
                    {statusLabel(p.status)}
                  </span>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Visualizar"><IconButton size="small" onClick={() => handleView(p)}><Visibility fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Exportar PDF"><IconButton size="small" onClick={() => handleDownloadPDF(p)}><PictureAsPdf fontSize="small" style={{ color: "#e53935" }} /></IconButton></Tooltip>
                  <Tooltip title="Enviar por WhatsApp"><IconButton size="small" onClick={() => openSendDialog(p)}><WhatsApp fontSize="small" style={{ color: "#25d366" }} /></IconButton></Tooltip>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => setDeleteModal({ open: true, id: p.id, name: p.proposalNumber })}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {proposals.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center">Nenhuma proposta encontrada</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Form Dialog */}
      {open && (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
          <DialogTitle>{editing ? `Editar ${editing.proposalNumber}` : "Nova Proposta"}</DialogTitle>
          <DialogContent>
            {!editing && templates.length > 0 && (
              <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
                <InputLabel>Usar Modelo (opcional)</InputLabel>
                <Select label="Usar Modelo (opcional)" value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)}>
                  <MenuItem value=""><em>— Sem modelo —</em></MenuItem>
                  {templates.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            <Paper variant="outlined">
              <Tabs
                value={formTab} onChange={(_, v) => setFormTab(v)}
                indicatorColor="primary" textColor="primary"
                variant="scrollable" scrollButtons="auto"
              >
                <Tab label="Dados Gerais" />
                <Tab label={`Itens (${form.items.length})`} />
                <Tab label="Apresentação" />
                <Tab label="Observações" />
                <Tab label="Orientações" />
                <Tab label="Termos e Condições" />
              </Tabs>
            </Paper>

            <Box className={classes.tabPanel}>
              {/* Tab 0: Dados Gerais */}
              <TabPanel value={formTab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <TextField fullWidth label="Título *" value={form.title} onChange={setField("title")} variant="outlined" size="small" autoFocus />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Status</InputLabel>
                      <Select label="Status" value={form.status} onChange={setField("status")}>
                        {STATUS_LIST.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Cliente</InputLabel>
                      <Select label="Cliente" value={form.clientId} onChange={setField("clientId")}>
                        <MenuItem value=""><em>Nenhum</em></MenuItem>
                        {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Responsável</InputLabel>
                      <Select label="Responsável" value={form.responsibleId} onChange={setField("responsibleId")}>
                        <MenuItem value=""><em>Nenhum</em></MenuItem>
                        {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth label="Emissão" value={form.issueDate} onChange={setField("issueDate")} variant="outlined" size="small" type="date" InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth label="Válida até" value={form.validUntil} onChange={setField("validUntil")} variant="outlined" size="small" type="date" InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Condições de Pagamento" value={form.paymentTerms} onChange={setField("paymentTerms")} variant="outlined" size="small" placeholder="Ex: 30/60/90 dias" />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 1: Itens */}
              <TabPanel value={formTab} index={1}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <Button size="small" startIcon={<AddCircle />} onClick={addItem} color="primary" variant="outlined">
                    Adicionar Item
                  </Button>
                </div>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ minWidth: 160 }}>Produto</TableCell>
                      <TableCell style={{ minWidth: 180 }}>Descrição</TableCell>
                      <TableCell style={{ width: 80 }} align="right">Qtd</TableCell>
                      <TableCell style={{ width: 110 }} align="right">Preço Unit.</TableCell>
                      <TableCell style={{ width: 80 }} align="right">Desc %</TableCell>
                      <TableCell style={{ width: 110 }} align="right">Total</TableCell>
                      <TableCell style={{ width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.items.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>
                          <Select fullWidth value={String(item.productId || "")} displayEmpty size="small" onChange={(e) => selectProduct(idx, e.target.value)}>
                            <MenuItem value=""><em>— Selecionar —</em></MenuItem>
                            {products.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField fullWidth value={item.description || ""} size="small" onChange={(e) => updateItem(idx, "description", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField type="number" value={item.quantity} size="small" inputProps={{ min: 0, step: 0.001, style: { textAlign: "right" } }} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField type="number" value={item.unitPrice} size="small" inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }} onChange={(e) => updateItem(idx, "unitPrice", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField type="number" value={item.discount} size="small" inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: "right" } }} onChange={(e) => updateItem(idx, "discount", e.target.value)} />
                        </TableCell>
                        <TableCell align="right">{fmt(calcItemTotal(item))}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeItem(idx)}><RemoveCircle fontSize="small" color="error" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {form.items.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center">Nenhum item. Clique em "Adicionar Item".</TableCell></TableRow>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} align="right"><strong>Subtotal:</strong></TableCell>
                      <TableCell align="right"><strong>{fmt(subtotal)}</strong></TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} align="right">Desconto global (R$):</TableCell>
                      <TableCell colSpan={2} align="right">
                        <TextField
                          type="number" value={form.discountValue} size="small"
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: "right", width: 100 } }}
                          onChange={(e) => setForm({ ...formRef.current, discountValue: e.target.value })}
                        />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} align="right"><strong style={{ fontSize: 16 }}>TOTAL:</strong></TableCell>
                      <TableCell align="right"><strong style={{ fontSize: 16 }}>{fmt(total)}</strong></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TabPanel>

              {/* Tab 2: Apresentação */}
              <TabPanel value={formTab} index={2}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Texto de apresentação / carta de cobertura exibida no início do PDF.
                </Typography>
                <TextField
                  fullWidth label="Apresentação" value={form.intro} onChange={setField("intro")}
                  variant="outlined" size="small" multiline rows={12}
                  placeholder="Prezado(a) cliente,&#10;&#10;Apresentamos nossa proposta comercial..."
                />
              </TabPanel>

              {/* Tab 3: Observações */}
              <TabPanel value={formTab} index={3}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Observações sobre o escopo, metodologia ou condições especiais.
                </Typography>
                <TextField
                  fullWidth label="Observações" value={form.notes} onChange={setField("notes")}
                  variant="outlined" size="small" multiline rows={12}
                />
              </TabPanel>

              {/* Tab 4: Orientações */}
              <TabPanel value={formTab} index={4}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Instruções ao cliente: antes, durante e após o serviço.
                </Typography>
                <TextField
                  fullWidth label="Orientações ao Cliente" value={form.orientacoes} onChange={setField("orientacoes")}
                  variant="outlined" size="small" multiline rows={12}
                  placeholder="ANTES DO SERVIÇO:&#10;• ...&#10;&#10;DURANTE O SERVIÇO:&#10;• ...&#10;&#10;APÓS O SERVIÇO:&#10;• ..."
                />
              </TabPanel>

              {/* Tab 5: Termos */}
              <TabPanel value={formTab} index={5}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Termos, condições gerais, garantias e avisos legais.
                </Typography>
                <TextField
                  fullWidth label="Termos e Condições" value={form.termos} onChange={setField("termos")}
                  variant="outlined" size="small" multiline rows={12}
                  placeholder="ATENÇÃO:&#10;• Os produtos utilizados são registrados no Ministério da Saúde...&#10;• ..."
                />
              </TabPanel>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} color="primary" variant="contained" disabled={!form.title}>Salvar</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* View Dialog */}
      {viewData && (
        <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>
            {viewData.proposalNumber} — {viewData.title}
            <span style={{ marginLeft: 12, color: STATUS_COLORS[viewData.status] || "#555", fontWeight: 600, fontSize: 14 }}>
              [{statusLabel(viewData.status)}]
            </span>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="body2"><strong>Cliente:</strong> {viewData.client ? viewData.client.name : "—"}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2"><strong>Responsável:</strong> {viewData.responsible ? viewData.responsible.name : "—"}</Typography></Grid>
              <Grid item xs={3}><Typography variant="body2"><strong>Emissão:</strong> {viewData.issueDate || "—"}</Typography></Grid>
              <Grid item xs={3}><Typography variant="body2"><strong>Validade:</strong> {viewData.validUntil || "—"}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2"><strong>Pagamento:</strong> {viewData.paymentTerms || "—"}</Typography></Grid>
            </Grid>
            <Divider style={{ margin: "12px 0" }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">Qtd</TableCell>
                  <TableCell align="right">Preço Unit.</TableCell>
                  <TableCell align="right">Desc %</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(viewData.items || []).map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.description || "—"}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{fmt(item.unitPrice)}</TableCell>
                    <TableCell align="right">{Number(item.discount) || 0}%</TableCell>
                    <TableCell align="right">{fmt(item.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right"><strong>Subtotal:</strong></TableCell>
                  <TableCell align="right"><strong>{fmt(viewData.subtotal)}</strong></TableCell>
                </TableRow>
                {Number(viewData.discountValue) > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="right">Desconto:</TableCell>
                    <TableCell align="right">- {fmt(viewData.discountValue)}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} align="right"><strong>TOTAL:</strong></TableCell>
                  <TableCell align="right"><strong style={{ fontSize: 15 }}>{fmt(viewData.total)}</strong></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            {viewData.notes && <><Divider style={{ margin: "12px 0" }} /><Typography variant="body2"><strong>Observações:</strong> {viewData.notes}</Typography></>}
            {viewData.orientacoes && <><Divider style={{ margin: "12px 0" }} /><Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}><strong>Orientações:</strong>{"\n"}{viewData.orientacoes}</Typography></>}
            {viewData.termos && <><Divider style={{ margin: "12px 0" }} /><Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}><strong>Termos:</strong>{"\n"}{viewData.termos}</Typography></>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleDownloadPDF(viewData)} startIcon={<PictureAsPdf />} style={{ color: "#e53935" }}>Exportar PDF</Button>
            <Button onClick={() => { setViewOpen(false); openSendDialog(viewData); }} startIcon={<WhatsApp />} style={{ color: "#25d366" }}>Enviar por WhatsApp</Button>
            <Button onClick={() => setViewOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* WhatsApp Send Dialog */}
      {sendOpen && (
        <Dialog open={sendOpen} onClose={() => !sending && setSendOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>
            <WhatsApp style={{ color: "#25d366", marginRight: 8, verticalAlign: "middle" }} />
            Enviar por WhatsApp — {sendProposal?.proposalNumber}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Telefone *" value={sendForm.phone}
                  onChange={(e) => setSendForm({ ...sendForm, phone: e.target.value })}
                  variant="outlined" size="small" placeholder="55119XXXXXXXX"
                  helperText="Somente números, com DDI+DDD"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Conexão WhatsApp *</InputLabel>
                  <Select label="Conexão WhatsApp *" value={sendForm.whatsappId} onChange={(e) => setSendForm({ ...sendForm, whatsappId: e.target.value })}>
                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                    {whatsapps.map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Mensagem" value={sendForm.message}
                  onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                  variant="outlined" size="small" multiline rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={sendForm.schedule} onChange={(e) => setSendForm({ ...sendForm, schedule: e.target.checked })} color="primary" />}
                  label={<span style={{ display: "flex", alignItems: "center", gap: 4 }}><ScheduleIcon fontSize="small" /> Agendar envio</span>}
                />
              </Grid>
              {sendForm.schedule && (
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth label="Data e hora do envio" type="datetime-local"
                    value={sendForm.sendAt} onChange={(e) => setSendForm({ ...sendForm, sendAt: e.target.value })}
                    variant="outlined" size="small" InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendOpen(false)} disabled={sending}>Cancelar</Button>
            <Button
              onClick={handleSendWhatsApp} color="primary" variant="contained"
              disabled={sending || !sendForm.whatsappId || !sendForm.phone}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : (sendForm.schedule ? <ScheduleIcon /> : <WhatsApp />)}
            >
              {sending ? "Processando..." : (sendForm.schedule ? "Agendar" : "Enviar Agora")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmationModal
        title="Excluir Proposta"
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: "" })}
        onConfirm={handleDelete}
      >
        Deseja excluir a proposta <strong>{deleteModal.name}</strong>?
      </ConfirmationModal>
    </div>
  );
}
